import { Navigate, useParams } from "react-router-dom";
import { useAccess, useCustomer } from "../../api/hooks";
import { useAuth } from "../../auth/AuthProvider";
import { EmptyState, NotAvailable } from "../../components/ui";
import { viewAsUrl } from "../../viewAs";
import { ACCESS_SECTIONS } from "../../lib/modules";
import { ModulePage } from "../modules/ModulePage";
import { UsersPage } from "../UsersPage";
import { EnvironmentsSection } from "./EnvironmentsSection";
import { GroupsSection } from "./GroupsSection";
import { PeopleSection } from "./PeopleSection";
import { RolesSection } from "./RolesSection";

const TITLES: Record<string, string> = { people: "Access · People", roles: "Access · Roles", groups: "Access · Groups", environments: "Access · Environments" };

/**
 * The Access module (ADR-0017 section 6): who holds what on this
 * environment (People), what a role lets its holder do (Roles), sets of
 * roles for several people at once (Groups), and the environment's chain
 * and switches (Environments). Each section is behind its own `enabled`
 * switch; the API refuses what the screen hides.
 */
export function AccessPage() {
  const { me } = useAuth();
  const { section = "people" } = useParams();
  const access = useAccess();
  const title = TITLES[section];
  if (!title) return <Navigate to=".." replace />;
  const needs = ACCESS_SECTIONS.find((s) => s.id === section)?.needs ?? [];
  return (
    <ModulePage module="access" section={section === "people" ? undefined : section} title={title}>
      {(_d, environmentId) => {
        if (!access.isPending && !access.set(environmentId).hasAny(needs)) {
          return <EmptyState title="This section is not available to you here" hint="No role you hold on this environment enables it." />;
        }
        // ADR-0013 / ADR-0017: roles and groups belong to the customer's organisation; Gairm staff reach them only
        // through the customer's portal (found on Claude's walk of dev, 2026-09-21: the API refuses staff here).
        if ((section === "roles" || section === "groups") && me?.role === "internal-team" && !me.viewAs) {
          return <StaffOpensTheirPortal environmentId={environmentId} what={section} />;
        }
        switch (section) {
          case "roles":
            return <RolesSection environmentId={environmentId} />;
          case "groups":
            return <GroupsSection environmentId={environmentId} />;
          case "environments":
            return <EnvironmentsSection environmentId={environmentId} />;
          default:
            return (
              <div className="grid gap-8">
                <PeopleSection environmentId={environmentId} />
                {(me?.role === "customer" || me?.viewAs) && <UsersPage embedded />}
              </div>
            );
        }
      }}
    </ModulePage>
  );
}

function StaffOpensTheirPortal({ environmentId, what }: { environmentId: string; what: "roles" | "groups" }) {
  const env = useCustomer(environmentId);
  const organisationId = env.data?.customer.organisation_id;
  return (
    <div className="grid gap-3">
      <NotAvailable what={what === "roles" ? "Roles" : "Groups"} because="here" why="Roles and groups belong to the customer's organization, so Gairm staff see and change them through the customer's own portal - every change is recorded under your name there. Gairm staff hold no role in a customer's organization." />
      {organisationId && (
        <a href={viewAsUrl(organisationId, `/access/${environmentId}/${what}`)} target="_blank" rel="opener" className="text-sm text-accent-strong underline dark:text-accent">
          Open their portal at Access › {what === "roles" ? "Roles" : "Groups"}
        </a>
      )}
    </div>
  );
}
